import {
    useQueries,
    type QueryKey,
    type UseQueryResult,
} from "@tanstack/react-query";
import { useMemo } from "react";

import type { Page } from "./api.types";

export interface PagedEntityParams {
    page: number;
    size: number;
    search: string;
}

export interface UsePagedEntityOptions<T extends { id: number }> {
    pageCount: number;
    size: number;
    search: string;
    queryKey: (params: PagedEntityParams) => QueryKey;
    queryFn: (params: PagedEntityParams) => Promise<Page<T>>;
    staleTime?: number;
    enabled?: boolean;
}

export function usePagedEntity<T extends { id: number }>({
    pageCount,
    size,
    search,
    queryKey,
    queryFn,
    staleTime = 30_000,
    enabled = true,
}: UsePagedEntityOptions<T>) {
    const normalizedSearch = search.trim();
    const combine = useMemo(
        () => (queries: UseQueryResult<Page<T>>[]) => {
            const items = [
                ...new Map(
                    queries
                        .flatMap((query) => query.data?.items ?? [])
                        .map((item) => [item.id, item])
                ).values(),
            ];
            const firstPage = queries[0]?.data;
            return {
                items,
                total: firstPage?.total ?? 0,
                totalPages: firstPage?.pages ?? 0,
                isInitialPending: enabled && (queries[0]?.isPending ?? true),
                isFetching: queries.some((query) => query.isFetching),
                isError: queries.some((query) => query.isError),
                error: queries.find((query) => query.isError)?.error ?? null,
                refetch: () =>
                    Promise.all(queries.map((query) => query.refetch())),
            };
        },
        [enabled]
    );

    return useQueries({
        queries: Array.from({ length: Math.max(1, pageCount) }, (_, index) => {
            const params = {
                page: index + 1,
                size,
                search: normalizedSearch,
            };
            return {
                queryKey: queryKey(params),
                queryFn: () => queryFn(params),
                staleTime,
                enabled,
            };
        }),
        combine,
    });
}
